import { Label, Typography } from '@neo4j-ndl/react';
import { CheckIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';

type Props = {
  isSelected: boolean;
  isTrialAvailable?: boolean;
  title: string;
  description: string;
  items: string[];
  footer: ReactNode;
  onSelect: () => void;
  isDisabled?: boolean;
  disabledReason?: { icon: ReactNode; reason: string };
  isReadOnly?: boolean;
  'data-testid': string;
};

export const TierCard = ({
  isSelected,
  title,
  description,
  isTrialAvailable = false,
  items,
  footer,
  onSelect,
  isDisabled = false,
  disabledReason,
  isReadOnly = false,
  'data-testid': dataTestId,
}: Props) => {
  const classes = classNames(
    'flex flex-col gap-4 rounded-lg p-6 border-neutral-border-weak border bg-neutral-bg-weak w-full text-left',
    {
      'border-primary-border-strong': isSelected,
      'hover:border-neutral-border-strong active:border-neutral-pressed': !isSelected && !isDisabled && !isReadOnly,
      'text-neutral-text-weakest': isDisabled,
      'hover:bg-neutral-hover active:bg-neutral-pressed': !isDisabled && !isReadOnly,
    },
  );

  return (
    <button className={classes} onClick={onSelect} disabled={isDisabled || isReadOnly} data-testid={dataTestId}>
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <Typography variant="subheading-large" className="@md:text-xl text-base">
            {title}
          </Typography>
          {isTrialAvailable && (
            <Label color="discovery" fill="semi-filled">
              Free trial
            </Label>
          )}
        </div>
        <Typography variant="body-medium" className={classNames({ 'text-neutral-text-weak': !isDisabled })}>
          {description}
        </Typography>
      </div>
      {/*  Body */}
      <ul className="[&>li:not(:last-child)]:mb-2">
        {items.map((item, index) => (
          <Typography
            variant="body-small"
            key={item + index}
            className="@md:text-xs flex items-center gap-2 text-xs"
            as="li"
          >
            <CheckIconOutline className={classNames('size-4', { 'text-primary-icon': !isDisabled })} /> {item}
          </Typography>
        ))}
      </ul>
      {/*  Footer */}
      <Typography
        variant="body-medium"
        className="@md:text-sm text-neutral-text-weak mt-auto flex justify-between gap-2 text-xs"
      >
        {isDisabled ? (
          <div className="flex items-center gap-1">
            {disabledReason?.icon} {disabledReason?.reason}
          </div>
        ) : (
          <>{footer}</>
        )}
      </Typography>
    </button>
  );
};
